// trailFilterBus — persistiert den TrailsPage-Filter-State über
// Route-Wechsel hinweg.
//
// Warum überhaupt persistieren:
//   App.tsx rendert die TrailsPage nur wenn pathname === '/trails' ist
//   (Overlay-Pattern — s. App.tsx für Gründe). Sobald der User z. B. auf die
//   Map zurückspringt, UNMOUNTED React die TrailsPage → alle useState-Werte
//   sind weg. Beim Rückkehr wird die Page neu gemountet und initialisiert den
//   Filter mit EMPTY_FILTER. Für den User fühlt sich das an als ob er seinen
//   Filter verloren hätte — obwohl die Map-Visibility-Maske (ein separater
//   Bus) noch aktiv ist. Dadurch sieht er "alle Trails" in der Liste, aber
//   nur 3 auf der Karte → inkonsistent und verwirrend.
//
// Design:
//   Ein Singleton hält die aktuelle Filter-Form. TrailsPage liest beim Mount
//   aus dem Bus (lazy init von useState), und schreibt bei jedem State-Update
//   zurück (useEffect). Auf Clear wird der Bus auf `null` gesetzt — das
//   entspricht EMPTY_FILTER und sorgt dafür, dass beim nächsten Mount kein
//   "geist-Filter" zurückkommt.
//
// Warum nicht nur ein Module-Level `let persistedFilter`:
//   Funktional äquivalent. Der Bus mit subscribe() kostet ~15 Zeilen mehr,
//   erlaubt aber zukünftig anderen Komponenten (z. B. einem Badge auf der
//   Trails-Tab, das "filter active" anzeigt) reaktiv auf Änderungen zu
//   reagieren, ohne weitere Refactors. Matcht außerdem das bestehende
//   Bus-Pattern (trailVisibilityBus, mapIntentsBus, compassBus), d. h.
//   minimaler Cognitive Load beim Lesen.

import type { Difficulty } from '@adtu/shared';

/** Filter-Form. Identisch zur lokalen Definition in TrailsPage, zentral
 *  gehalten damit Bus & Consumer sicher den gleichen Shape sprechen.
 *
 *  - `maxDistKm`: `null` → Entfernung egal.
 *  - `difficulties`: leeres Set → alle Schwierigkeiten (außer wenn hideAll).
 *  - `query`: leerer String → kein Name-Match-Filter (Wave 14bz).
 *  - `hideAll`: Wave 14cf-fix15 — explizites "Trails komplett verbergen".
 *    User-Wunsch: "None"-Preset im DifficultyLegend soll alle Trails von der
 *    Karte nehmen. Vorher wurde "None" als "leerer Set = kein Filter = alles
 *    zeigen" interpretiert (semantischer Konflikt). Mit dem `hideAll`-Flag
 *    haben wir eine eindeutige Markierung: true → MapPage filtert alles weg,
 *    egal was in difficulties steht. */
export interface TrailFilter {
  maxDistKm: number | null;
  difficulties: Set<Difficulty>;
  query: string;
  hideAll: boolean;
}

/** Factory für den Null-State. Wir geben immer ein frisches Set raus, damit
 *  Consumer es ohne Mutation-Angst klonen/mergen können. */
export function emptyTrailFilter(): TrailFilter {
  return {
    maxDistKm: null,
    difficulties: new Set<Difficulty>(),
    query: '',
    hideAll: false,
  };
}

/** Kurz-Check ob irgendein Filter-Feld aktiv ist. Muss sich mit dem
 *  TrailsPage-Handling decken (dort auch als `isFilterActive` dupliziert —
 *  beide delegieren an diese gemeinsame Regel). */
export function isTrailFilterActive(f: TrailFilter): boolean {
  return (
    f.maxDistKm != null ||
    f.difficulties.size > 0 ||
    f.query.trim().length > 0 ||
    f.hideAll === true
  );
}

type Listener = (f: TrailFilter | null) => void;

class TrailFilterBus {
  private current: TrailFilter | null = null;
  private readonly listeners = new Set<Listener>();

  /** Aktueller Snapshot — `null` = kein Filter aktiv (äquivalent zu
   *  emptyTrailFilter(), aber spart eine leere Set-Allokation). */
  get(): TrailFilter | null {
    return this.current;
  }

  /** Setzt die Filter-Form. `null` = explizit "kein Filter". */
  set(f: TrailFilter | null): void {
    this.current = f;
    for (const l of this.listeners) l(this.current);
  }

  /** Convenience — "clear" ist klarer als `set(null)`. */
  clear(): void {
    this.set(null);
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
}

export const trailFilterBus = new TrailFilterBus();
